<script setup lang="ts">
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { formatIsoDate } from "~/core/utils/date";
import type { RelationEntity } from "~/modules/relation/models/RelationEntity.interface";
import type { RelationReferentUser } from "~/modules/relation/models/RelationReferentUser.interface";
import ILxcLink2 from "~icons/lxc/link-2";
import ILxcLogIn from "~icons/lxc/log-in";
import ILxcTrash2 from "~icons/lxc/trash-2";
import ILxcUserCheck from "~icons/lxc/user-check";
import ILxcUserX from "~icons/lxc/user-x";

const props = defineProps<{
  fromTo: "from" | "to";
  relationEntity: RelationEntity;
  /**
   * Factory function that creates delete functions for relation entities.
   *
   * This function takes an organization code and returns a parameterless async function
   * that will delete the relation with that specific organization. The returned function
   * is then passed to the DeleteAction component which handles the confirmation dialog,
   * loading state, error handling, and success notifications.
   *
   * @param organizationCode - The code of the target organization to delete
   * @returns An async function that performs the deletion when executed
   *
   * @example
   * // Parent component provides the factory
   * <relation-entity :delete-fn="getDeleteFn" />
   *
   * // This component calls the factory with the relation's organization code
   * const deleteSpecificRelation = deleteFn(relationEntity.organizationCode);
   *
   * // DeleteAction component receives and executes the function
   * <delete-action :delete-fn="deleteSpecificRelation" />
   */
  deleteFn?: (organizationCode: number) => () => Promise<void>;
}>();

defineEmits(["deleteSuccess"]);

const { can } = useAcl();

const canDeleteRelation = computed(() => can(ACLRoles.CAN_WRITE_RELATION));

interface RelationConfiguration {
  fromToIconClass: string[];
  entityName: string;
  relationStartDate: string;
  referentUser?: RelationReferentUser;
}

const fromToIconClass: ComputedRef<string[]> = computed(() => {
  const iconClass = ["text-gray-700"];
  if (props.fromTo === "to") {
    iconClass.push("rotate-180");
  }
  return iconClass;
});

const relationConfiguration: ComputedRef<RelationConfiguration> = computed(
  () => {
    return {
      fromToIconClass: fromToIconClass.value,
      entityName: props.relationEntity.organizationName,
      relationStartDate: props.relationEntity.createdAt,
      referentUser: props.relationEntity.referentUser,
    };
  },
);
</script>

<template>
  <div
    class="flex items-center justify-between border border-gray-400 rounded-lg px-3 py-1.5 gap-6"
  >
    <div class="flex gap-2 items-center flex-1">
      <i-lxc-log-in
        width="20"
        height="20"
        :class="relationConfiguration.fromToIconClass"
      />
      <span class="border border-gray-300 rounded px-3.5 py-1.5 flex-1">
        {{ relationConfiguration.entityName }}
      </span>
    </div>
    <div class="flex gap-2 items-center">
      <i-lxc-link-2 class="text-gray-700" />
      <span>
        {{
          $t("administration.relation.since", {
            date: formatIsoDate(
              relationConfiguration.relationStartDate,
              $t("dateFormat.date"),
            ),
          })
        }}
      </span>
    </div>

    <div class="grid grid-cols-[auto_24px] gap-4 ml-2">
      <lxc-dropdown-panel
        title=""
        button-type="borderless"
        button-class="!px-0 !py-0"
        trigger="hover"
      >
        <template #button-label>
          <span class="block w-6 text-gray-700">
            <i-lxc-user-check v-if="relationConfiguration.referentUser" />
            <i-lxc-user-x v-else />
          </span>
        </template>

        <template #default>
          <div
            v-if="relationConfiguration.referentUser"
            class="flex flex-col gap-2"
          >
            <div>
              <span>
                {{ $t("administration.relations.contact.administrator") }}&nbsp;
              </span>
              <span class="font-semibold">
                {{
                  `${relationConfiguration.referentUser?.firstName} ${relationConfiguration.referentUser?.lastName}`
                }}
              </span>
            </div>
            <div class="flex gap-4 justify-between items-center">
              <span class="font-semibold">{{
                relationConfiguration.referentUser?.email
              }}</span>
              <lxc-copy-to-clipboard
                :title="$t('button.copiedTitle')"
                :info-to-copy="relationConfiguration.referentUser?.email"
                class="copy-to-clipboard-button"
              />
            </div>
          </div>
          <div v-else>
            {{ $t("administration.relations.contact.noContact") }}
          </div>
        </template>
      </lxc-dropdown-panel>
      <delete-action
        v-if="deleteFn && canDeleteRelation"
        button-class="!px-0 !py-0"
        :title="$t('administration.relation.delete.confirm.title')"
        :confirm-title="$t('administration.relation.delete.confirm.title')"
        :confirm-description="
          $t('administration.relation.delete.confirm.description', {
            name: relationConfiguration.entityName,
          })
        "
        :icon="ILxcTrash2"
        icon-class="text-gray-700 w-6 h-6"
        :icon-size="24"
        :delete-fn="deleteFn(relationEntity.organizationCode)"
        @success="$emit('deleteSuccess')"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.copy-to-clipboard-button {
  :deep(button) {
    padding: 0 !important;
  }
}
</style>
